import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Lock, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';

const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_8152f177-ab44-4731-90d4-eb859619e536/artifacts/rspaa7p7_Ads%C4%B1z%20tasar%C4%B1m%20%2811%29.png';
const PET_IMAGE = 'https://images.unsplash.com/photo-1640161415278-a5ac46f82d04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxjdXRlJTIwZG9nJTIwdmV0ZXJpbmFyeSUyMGNsaW5pYyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3Mzg5NTUyMHww&ixlib=rb-4.1.0&q=85';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(phone, password);
    if (result.success) {
      const savedUser = JSON.parse(localStorage.getItem('agrosvet_user'));
      if (savedUser?.role === 'customer') {
        navigate('/customer-panel');
      } else {
        navigate('/panel');
      }
    } else {
      setError(result.error || 'Telefon numarası veya şifre hatalı');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#5a5bd6] via-[#6b6de3] to-[#8b8ff0] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorative circles */}
      <div className="absolute top-[-120px] right-[-80px] w-[400px] h-[400px] bg-white/[0.04] rounded-full" />
      <div className="absolute bottom-[-150px] left-[-100px] w-[500px] h-[500px] bg-white/[0.03] rounded-full" />

      {/* Back to home */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-5 left-5 flex items-center gap-1.5 text-white/70 hover:text-white text-sm transition-colors"
        data-testid="login-back-home"
      >
        <ArrowLeft size={16} /> Ana Sayfa
      </button>

      {/* Login Card */}
      <div className="w-full max-w-[860px] bg-white rounded-3xl shadow-2xl overflow-hidden flex relative z-10" data-testid="login-card">
        {/* Left - Image with overlay */}
        <div className="hidden md:block w-[46%] relative">
          <img src={PET_IMAGE} alt="Pet" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#6b6de3]/80 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-7">
            <div className="flex items-center gap-3 mb-2">
              <img src={LOGO_LIGHT} alt="" className="w-10 h-10 object-contain" />
              <span className="text-white font-extrabold text-lg tracking-tight">AGROSVET</span>
            </div>
            <p className="text-white/80 text-xs leading-relaxed">
              Kliniğinizi yönetmenin en kolay yolu.
            </p>
          </div>
        </div>

        {/* Right - Form */}
        <div className="flex-1 px-8 sm:px-12 py-10 flex flex-col justify-center">
          {/* Mobile logo */}
          <div className="flex flex-col items-center mb-6 md:hidden">
            <img src={LOGO_LIGHT} alt="AGROSVET" className="w-14 h-14 object-contain mb-2" style={{filter: 'brightness(0.4) sepia(1) hue-rotate(220deg) saturate(5)'}} />
            <span className="font-extrabold text-[#6b6de3] text-base">AGROSVET</span>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-1" data-testid="login-title">
            Hoş geldiniz
          </h2>
          <p className="text-sm text-gray-400 mb-7">Kliniğinize giriş yapın</p>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl text-center mb-5 animate-fade-in" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Telefon</label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6b6de3]/50" size={17} />
                <input
                  id="phone"
                  type="tel"
                  placeholder="05XXXXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  inputMode="numeric"
                  maxLength={11}
                  className="w-full h-12 pl-11 pr-4 text-sm bg-[#F8F8FC] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6b6de3]/30 transition-all placeholder:text-gray-300"
                  required
                  data-testid="login-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6b6de3]/50" size={17} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Şifrenizi girin"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 pl-11 pr-11 text-sm bg-[#F8F8FC] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6b6de3]/30 transition-all placeholder:text-gray-300"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#6b6de3] focus:ring-[#6b6de3] cursor-pointer"
                  data-testid="login-remember-checkbox"
                />
                <label htmlFor="rememberMe" className="text-sm text-gray-500 cursor-pointer select-none">
                  Beni hatırla
                </label>
              </div>
              <button type="button" className="text-xs text-[#6b6de3] hover:underline font-medium" data-testid="login-forgot-btn">
                Şifremi unuttum
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-sm font-bold bg-[#6b6de3] hover:bg-[#5658c4] rounded-full transition-all shadow-lg shadow-[#6b6de3]/25 mt-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Giriş yapılıyor...
                </>
              ) : (
                'Giriş Yap'
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            Hesabınız yok mu?{' '}
            <button onClick={() => navigate('/')} className="text-[#6b6de3] hover:underline font-semibold">
              Ücretsiz deneyin
            </button>
          </p>

          <div className="mt-3 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400 mb-1">Pet sahibi misiniz?</p>
            <button
              onClick={() => navigate('/customer-login')}
              className="text-sm text-[#4EB8A1] hover:underline font-semibold"
              data-testid="login-customer-link"
            >
              Pet sahibi girişi
            </button>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-white/40 mt-6 relative z-10">
        &copy; {new Date().getFullYear()} AgrosTech. Tüm hakları saklıdır.
      </p>
    </div>
  );
};

export default Login;
